import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import ChangeLanguage from "@/components/ui/shared/change-language";
import { XCircle } from "lucide-react";

// We use search params to determine the specific error message
type VerifyErrorParams = {
  reason?: "expired" | "invalid" | "already-verified";
};

export const Route = createFileRoute("/(onboarding)/auth/verify-failed")({
  validateSearch: (search: Record<string, unknown>): VerifyErrorParams => ({
    reason: (search.reason as VerifyErrorParams["reason"]) || "invalid",
  }),
  component: VerifyFailed,
});

function VerifyFailed() {
  const { t } = useTranslate();
  const { reason } = Route.useSearch();

  const errorContent = {
    expired: {
      title: t("Link Expired"),
      description: t(
        "For security reasons, verification links are only valid for a limited time. Please request a new one."
      ),
    },
    invalid: {
      title: t("Invalid Link"),
      description: t(
        "The link you followed appears to be broken or has already been used. Please check your email or try again."
      ),
    },
    "already-verified": {
      title: t("Already Active"),
      description: t(
        "Your account has already been verified! You can proceed to the login page."
      ),
    },
  }[reason || "invalid"];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-1/2 hidden md:block relative overflow-hidden border-r border-gray-100 bg-white">
        <div className="absolute top-8 left-8">
          <img src="/assets/images/logo.svg" alt="Logo" width={140} />
        </div>
        <div className="flex items-center  bg-gray-50 justify-center h-screen">
          <img
            src="/assets/images/verify-failed.svg"
            alt="Verification Failed"
            className="w-1/2"
          />
        </div>
      </div>

      {/* Right side: Error Content */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 bg-white relative">
        <div className="absolute top-8 right-8">
          <ChangeLanguage />
        </div>

        <div className="w-full max-w-md text-center">
          {/* Error Icon */}
          <div className="mx-auto w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>

          <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-3">
            {errorContent.title}
          </h1>

          <p className="text-gray-600 mb-8 leading-relaxed">
            {errorContent.description}
          </p>

          <div className="space-y-4">
            {reason !== "already-verified" ? (
              <Link to="/auth/verify-account">
                <Button className="">{t("Request New Link")}</Button>
              </Link>
            ) : (
              <Link to="/auth/login">
                <Button className="w-full">{t("Go to Login")}</Button>
              </Link>
            )}

            <div className="pt-6">
              <Link
                to="/auth/login"
                className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
              >
                {t("Back to login")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
